import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import T from 'prop-types';
import { map, includes } from 'lodash-es';

import AppIcon from '../../../../../components/AppIcon';
import SearchTypeahead from '../../../../../components/SearchTypeahead';

const documentBody = document.body;

const AddAppModal = ({
  topPopularApps,
  selectedApps,
  selectionForbidden,
  onClose,
  onChange,
  onForbiddenStatusChange,
  windowY,
}) => {
  const searchTypeaheadRef = useRef();

  const handlePreventScroll = (action) => {
    document.documentElement.style.top = `-${windowY}px`;
    document.documentElement.classList[action]('pos-fixed');
    document.documentElement.classList[action]('w-100');
  };

  const scrollToY = () => {
    document.documentElement.style.top = '0px';
    window.scrollY = window.scrollTo(0, windowY);
  };

  useEffect(() => {
    handlePreventScroll('add');
    return () => {
      handlePreventScroll('remove');
      scrollToY();
    };
  }, []);

  const handlePopularAppSelect = useCallback((app) => {
    onChange((prevApps) => {
      if (!prevApps.some(({ ref_no }) => `${ref_no}` === `${app.ref_no}`)) {
        return [...prevApps, app];
      }
      return prevApps;
    });
    onClose();
  }, []);

  // Search
  const handleChange = useCallback(
    ([app]) => {
      handlePopularAppSelect(app);
    },
    [handlePopularAppSelect]
  );

  const handleItemSelected = useCallback(
    (app) => ({
      itemSelected: app.dls_30d === 0 || includes(map(selectedApps, 'ref_no'), app.ref_no),
    }),
    [selectedApps]
  );

  const handleSearch = useCallback(
    (results, searchQuery, forbidden) => {
      if (!selectionForbidden && forbidden) onForbiddenStatusChange(forbidden);
    },
    [onForbiddenStatusChange]
  );

  const filterSearchResults = useCallback(
    (results) => results.filter((app) => app.dls_30d !== 0),
    []
  );

  return createPortal(
    <div className="add-app-modal">
      <div className="add-app-modal__overlay animation-fade-element" />

      <div className="add-app-modal__inner animation-slide-element">
        <header className="add-app-modal__header">
          <h2 className="add-app-modal__title">Add an App</h2>
          <div className="add-app-modal__close" onClick={onClose}>
            <span className="hicon-close" />
          </div>
        </header>
        <section className="add-app-modal__body">
          <div className="add-app-modal__search">
            <SearchTypeahead
              onChange={handleChange}
              onSearchCallback={handleSearch}
              itemSelected={handleItemSelected}
              decorateItems={filterSearchResults}
              ref={searchTypeaheadRef}
              searchAsset="apps"
              maxHeight="210px"
              autoFocus
              placeholder="Search for any apps"
              decorators={['dls_30d', 'big_icon_url']}
              disabled={selectionForbidden}
              minLength={1}
              disableSearchOnFocus
              disableRecentResults
              includesUnified
            />
          </div>
          <div className="popular-apps">
            <h6 className="popular-apps__title">Popular Apps</h6>
            <div className="popular-apps__list">
              {topPopularApps.slice(0, 8).map((app) => (
                <div
                  key={app.ref_no}
                  className="popular-apps__app-box"
                  onClick={() => handlePopularAppSelect(app)}
                >
                  <AppIcon
                    url={app.big_icon_url || app.icon_url}
                    store={app.store}
                    size={48}
                    alt={app.name}
                    containerClasses="popular-apps__app-item"
                    hideHtmlTitle
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>,
    documentBody
  );
};

AddAppModal.propTypes = {
  selectedApps: T.array.isRequired,
  topPopularApps: T.array.isRequired,
  selectionForbidden: T.bool.isRequired,
  onClose: T.func.isRequired,
  onChange: T.func.isRequired,
  onForbiddenStatusChange: T.func.isRequired,
};

export default AddAppModal;
